import type { Item } from './api.ts';

export type SearchGroupKey = 'movie' | 'show' | 'episode' | 'season' | 'music';

export interface SearchGroup {
  key: SearchGroupKey;
  label: string;
  items: Item[];
}

const GROUP_ORDER: SearchGroupKey[] = ['movie', 'show', 'episode', 'season', 'music'];

const GROUP_LABEL: Record<SearchGroupKey, string> = {
  movie: 'Filmler',
  show: 'Diziler',
  episode: 'Bölümler',
  season: 'Sezonlar',
  music: 'Müzik',
};

/**
 * Arama sonuçlarını medya türüne göre gruplar.
 *
 * Müzik kütüphanesindeki parçalar katalogda `movie` türüyle tutulur; bu yüzden
 * yalnız `item.kind` yetmez. Kütüphane kimlikleri verilirse ses içerikleri
 * "Müzik" grubuna taşınır ve kart "FİLM" rozeti göstermez.
 */
export function groupSearchHits(
  hits: Item[],
  musicLibraryIds: ReadonlySet<string> = new Set(),
): SearchGroup[] {
  const buckets = new Map<SearchGroupKey, Item[]>();
  for (const key of GROUP_ORDER) buckets.set(key, []);

  for (const item of hits) {
    const key: SearchGroupKey = musicLibraryIds.has(item.libraryId)
      ? 'music'
      : (item.kind as SearchGroupKey);
    buckets.get(key)?.push(item);
  }

  return GROUP_ORDER.map((key) => ({
    key,
    label: GROUP_LABEL[key],
    items: buckets.get(key) ?? [],
  })).filter((group) => group.items.length > 0);
}

/** Tür ve izlenme filtresini uygular; `all` türü tüm grupları bırakır. */
export function filterSearchHits(
  hits: Item[],
  options: {
    kind: 'all' | SearchGroupKey;
    unwatchedOnly: boolean;
    musicLibraryIds?: ReadonlySet<string>;
  },
): Item[] {
  return hits.filter((item) => {
    if (options.unwatchedOnly && item.isWatched) return false;
    if (options.kind === 'all') return true;
    if (options.kind === 'music') {
      return options.musicLibraryIds?.has(item.libraryId) === true;
    }
    return item.kind === options.kind;
  });
}
